import React, { FC, useCallback, use, useEffect, useMemo, useState, useTransition } from "react";
import { Button, Card, Col, Flex, Input, Popconfirm, Row, Space, Tag, Tooltip } from "antd";
import { FormattedMessage, useIntl } from "react-intl";

import { ProductKeys } from "scripts/models/Product";
import { useCanWrite } from "scripts/infrastructure/hooks/usePermissions";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import RecordTextTemplateForm from "./RecordTextTemplateForm";
import { GQL } from "@binale-tech/shared";
import { ProductAccessUtils } from "../../../../scripts/models/utils/ProductAccessUtils";
import { useDebounce } from "../../../../scripts/infrastructure/hooks/useDebounce";
import { baseOptions } from "@app/components/shared/AgGridTable/options/baseOptions";
import {
    ColDef,
    ColGroupDef,
    GetRowIdParams,
    GridOptions,
    ICellRendererParams,
    IHeaderParams,
} from "ag-grid-community";
import { GenericRecord } from "../../../../scripts/models";
import { AgGridTable } from "@app/components/shared/AgGridTable";
import { Container } from "@app/components/shared/appearance/page";
import { KontoContext } from "../../../../scripts/context/accountingData/KontoEntitiesProvider";
import { gql } from "@apollo/client";
import { useGqlMutator } from "../../../../scripts/graphql/useGqlMutator";
import { CompanyContext } from "../../../../scripts/context/CompanyContext";
import { Multiselect } from "@app/components/shared/Multiselect/Multiselect";
import { Kbd } from "@heroui/react";

const genericRecordTextTemplatesDelete = gql`
    mutation genericRecordTextTemplatesDelete($input: GenericRecordTextTemplatesDeleteInput!) {
        genericRecordTextTemplatesDelete(input: $input)
    }
`;

type CellActionsProps = { onEdit: () => void; onDelete: () => void };

const getInitialValues = (type: GQL.ITextTemplateType): GQL.IGenericRecordTextTemplate => ({
    type,
    text: "",
    companyId: undefined,
    id: undefined,
    productIds: [...ProductAccessUtils.getAccountingKeys()] as GQL.IProductKey[],
    kbIds: [],
    bankIds: [],
});

const gridOptions = {
    ...baseOptions,
    defaultColDef: {
        ...baseOptions.defaultColDef,
        filter: "agTextColumnFilter",
        suppressHeaderFilterButton: true,
        cellRenderer: ({ value }: ICellRendererParams<GQL.IGenericRecordTextTemplate>) => {
            return <div>{value ?? <>&nbsp;</>}</div>;
        },
        autoHeight: true,
    },
    rowSelection: undefined,
} as GridOptions<GenericRecord>;

const HeaderActions: React.FC<IHeaderParams<GQL.IGenericRecordTextTemplate>> = () => {
    return (
        <div>
            <FormattedMessage id={"app.fields.actions"} />
        </div>
    );
};
const CellRendererActions: React.FC<ICellRendererParams<GQL.IGenericRecordTextTemplate> & CellActionsProps> = ({
    data,
    onEdit,
    onDelete,
}) => {
    return (
        <Flex gap={5} align="center" style={{ marginTop: 5 }}>
            <Button icon={<EditOutlined />} type="text" shape="circle" onClick={onEdit} />
            <Popconfirm
                onConfirm={onDelete}
                title={<FormattedMessage id="app.confirmation.header" />}
                okText={<FormattedMessage id="app.button.confirm" />}
                cancelText={<FormattedMessage id="app.button.cancel" />}
            >
                <Button icon={<DeleteOutlined />} type="text" shape="circle" />
            </Popconfirm>
        </Flex>
    );
};
const CellRendererProductIds: React.FC<ICellRendererParams<GQL.IGenericRecordTextTemplate>> = ({ api, data, node }) => {
    const { companyGQL } = use(CompanyContext);
    const { bankList, kasseList } = companyGQL;
    return (
        <>
            {data.productIds.map(v => {
                return (
                    <Tooltip
                        destroyOnHidden
                        key={v}
                        title={
                            <Space direction="vertical">
                                <FormattedMessage id={"app.titles." + v} />
                                {v === GQL.IProductKey.Kb &&
                                    (data.kbIds ?? [])
                                        .map(id => {
                                            const item = kasseList.filter(k => k.id === id).pop();
                                            if (!item) {
                                                return null;
                                            }
                                            return (
                                                <Space key={id}>
                                                    <Kbd>{item.accountNum}</Kbd> <span>{item.name}</span>
                                                </Space>
                                            );
                                        })
                                        .filter(Boolean)}
                                {v === GQL.IProductKey.Bank &&
                                    (data.bankIds ?? [])
                                        .map(id => {
                                            const item = bankList.filter(k => k.id === id).pop();
                                            if (!item) {
                                                return null;
                                            }
                                            return (
                                                <Space key={id}>
                                                    <Kbd>{item.accountNum}</Kbd> <span>{item.name}</span>
                                                </Space>
                                            );
                                        })
                                        .filter(Boolean)}
                            </Space>
                        }
                    >
                        <Tag>{v}</Tag>
                    </Tooltip>
                );
            })}
        </>
    );
};
const RecordTextTemplatesView: FC<{ isText2?: boolean }> = ({ isText2 }) => {
    const intl = useIntl();
    const mutator = useGqlMutator();
    const { companyGQL } = use(CompanyContext);
    const companyId = companyGQL?.id;
    const { recordTextTemplates } = use(KontoContext);

    const [inputValue, setInputValue] = useState("");
    const [textFilter, setTextFilter] = useState("");
    const [productFilter, setProductFilter] = useState<Set<string>>(new Set(ProductAccessUtils.getAccountingKeys()));

    const [activeItem, setActiveItem] = useState<GQL.IGenericRecordTextTemplate>(null);
    const handleClear = React.useCallback(() => setActiveItem(null), []);
    const canWrite = useCanWrite(ProductKeys.AccountingCommon);
    const [isPending, startTransition] = useTransition();

    const debouncedInput = useDebounce(inputValue, 250);

    useEffect(() => {
        startTransition(() => {
            setTextFilter(debouncedInput);
        });
    }, [setTextFilter, debouncedInput]);
    useEffect(() => {
        handleClear();
        // reset the form on type change
    }, [isText2, handleClear]);

    const handleDelete = useCallback(
        (id: string) => {
            if (id === activeItem?.id) {
                handleClear();
            }
            mutator.mutate<"genericRecordTextTemplatesDelete", GQL.IGenericRecordTextTemplatesDeleteInput>({
                mutation: genericRecordTextTemplatesDelete,
                input: {
                    ids: [id],
                    companyId,
                },
            });
        },
        [activeItem?.id, mutator, companyId, handleClear]
    );

    const rowData = useMemo(() => {
        return recordTextTemplates
            .filter(r => {
                if (!textFilter) {
                    return true;
                }
                return r.text.toLowerCase().includes(textFilter.toLowerCase());
            })
            .filter(v => {
                if (!isText2) {
                    return v.type === GQL.ITextTemplateType.Text1;
                }
                return v.type === GQL.ITextTemplateType.Text2;
            })
            .filter(v => {
                return v.productIds.some(p => productFilter.has(p));
            });
    }, [productFilter, isText2, textFilter, recordTextTemplates]);

    const columns: (ColDef<GQL.IGenericRecordTextTemplate> | ColGroupDef<GQL.IGenericRecordTextTemplate>)[] =
        useMemo(() => {
            const defs: (ColDef<GQL.IGenericRecordTextTemplate> | ColGroupDef<GQL.IGenericRecordTextTemplate>)[] = [
                {
                    field: "text",
                    headerName: "Text",
                    width: 650,
                    // filter: false,
                    sortable: true,
                    resizable: true,
                    suppressMovable: true,
                    suppressNavigable: true,
                    cellRenderer: ({ api, data, node }: ICellRendererParams<GQL.IGenericRecordTextTemplate>) => {
                        return data.text;
                    },
                    cellStyle: () => ({ paddingLeft: 5, paddingRight: 5 }),
                },
                {
                    field: "productIds",
                    headerName: intl.formatMessage({ id: "app.fields.products" }),
                    width: 350,
                    // filter: false,
                    sortable: true,
                    resizable: true,
                    suppressMovable: true,
                    suppressNavigable: true,
                    cellRenderer: CellRendererProductIds,
                    cellStyle: () => ({ paddingLeft: 5, paddingRight: 5 }),
                },
                {
                    headerComponent: HeaderActions,
                    cellRenderer: CellRendererActions,
                    cellRendererParams: (
                        params: ICellRendererParams<GQL.IGenericRecordTextTemplate>
                    ): CellActionsProps => ({
                        onEdit: () => setActiveItem(params.node.data),
                        onDelete: () => handleDelete(params.data.id),
                    }),
                    field: "actions" as any,
                    floatingFilter: false,
                    resizable: false,
                    suppressMovable: true,
                    suppressNavigable: true,
                    width: 100,
                    pinned: "right",
                    sortable: false,
                    lockPosition: true,
                },
            ];

            return defs;
        }, [handleDelete, intl]);

    return (
        <div className="ManageAccount__container">
            <Flex style={{ marginTop: 16, marginBottom: 16, justifyContent: "space-between" }}>
                <Space>
                    <Input.Search
                        style={{ width: "230px" }}
                        onChange={e => setInputValue(e.target.value)}
                        value={inputValue}
                        loading={isPending}
                    />
                    <Multiselect
                        includeSelectAllOption
                        onChange={v => {
                            setProductFilter(new Set(v.filter(v => v.selected).map(v => v.value)));
                        }}
                        data={[...ProductAccessUtils.getAccountingKeys()].map(value => ({
                            value,
                            label: intl.formatMessage({ id: "app.titles." + value }),
                            selected: productFilter.has(value),
                        }))}
                    />
                </Space>
            </Flex>
            <Row gutter={32} style={{ flexGrow: 1 }}>
                <Col span={16}>
                    <Container absolute>
                        {(w, h) => (
                            <AgGridTable
                                rowData={rowData}
                                wrapperStyles={{ height: h, width: w }}
                                columnDefs={columns}
                                gridOptions={gridOptions}
                                // rowClassRules={rowClassRules}
                                getRowId={({ data }: GetRowIdParams<GQL.IGenericRecordTextTemplate>) => {
                                    return data.id;
                                }}
                            />
                        )}
                    </Container>
                </Col>
                <Col span={1}></Col>
                <Col span={7}>
                    <Card
                        style={
                            activeItem
                                ? {
                                      height: "100%",
                                      overflow: "hidden",
                                      position: "absolute",
                                      width: "calc(100% - 32px)",
                                  }
                                : undefined
                        }
                        styles={{
                            body: {
                                height: "calc(100% - 56px)",
                                overflowY: "scroll",
                            },
                        }}
                        title={
                            <Flex style={{ width: "100%" }} justify={"space-between"} align="center" gap={10}>
                                <FormattedMessage id={isText2 ? "app.fields.buchtext2" : "app.fields.buchtext"} />

                                {activeItem ? (
                                    <Button onClick={handleClear}>
                                        <FormattedMessage id="app.button.cancel" />
                                    </Button>
                                ) : (
                                    <Button
                                        icon={<PlusOutlined />}
                                        type="primary"
                                        onClick={() =>
                                            setActiveItem(
                                                getInitialValues(
                                                    isText2 ? GQL.ITextTemplateType.Text2 : GQL.ITextTemplateType.Text1
                                                )
                                            )
                                        }
                                    >
                                        <FormattedMessage id="app.button.create" />
                                    </Button>
                                )}
                            </Flex>
                        }
                    >
                        {activeItem && (
                            <RecordTextTemplateForm disabled={!canWrite} data={activeItem} onClose={handleClear} />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
export default React.memo(RecordTextTemplatesView);
